import { useRef, useState } from "react";
import { X } from "@thuthi/ui/icons";
import { useTranslation } from "react-i18next";
import { Button } from "@thuthi/ui/components/button";
import { Dialog, DialogContent, DialogTitle } from "@thuthi/ui/components/dialog";
import { Input } from "@thuthi/ui/components/input";
import { splitNoEmpty } from "../app/utils";
import { rawEmojis, type RawEmoji } from "../app/emojis";

/** A raw emoji augmented with the pre-computed lowercase search string. */
type SearchableEmoji = RawEmoji & { searchBase: string };

// Create emoji list by category and create a search base (string with all search words)
//
// This also filters emojis that are not supported by Desktop Chrome.
// This is a hack, but on Ubuntu 18.04, with Chrome 99, only Emoji <= 11 are supported.

const emojisByCategory: Record<string, SearchableEmoji[]> = {};
const isDesktopChrome = /Chrome/.test(navigator.userAgent) && !/Mobile/.test(navigator.userAgent);
const maxSupportedVersionForDesktopChrome = 11;
rawEmojis.forEach((emoji) => {
  if (!emojisByCategory[emoji.category]) {
    emojisByCategory[emoji.category] = [];
  }
  try {
    const unicodeVersion = parseFloat(emoji.unicode_version);
    const supportedEmoji = unicodeVersion <= maxSupportedVersionForDesktopChrome || !isDesktopChrome;
    if (supportedEmoji) {
      const searchBase = `${emoji.description.toLowerCase()} ${emoji.aliases.join(" ")} ${emoji.tags.join(" ")}`;
      const emojiWithSearchBase = { ...emoji, searchBase };
      emojisByCategory[emoji.category].push(emojiWithSearchBase);
    }
  } catch (e) {
    // Nothing. Ignore.
  }
});

// MUI 시절에는 anchorEl 에 붙는 Popper 였다. shadcn 에는 popover 부품이 없어 Dialog 로 대체하고,
// anchorEl 은 열림 여부 신호로만 사용한다 (호출부 API 는 anchorEl/onClose/onEmojiPick 그대로).
// Escape 는 Radix 기본 동작으로 닫히므로 뒤로가기 브리지(hooks.js)가 함께 동작한다.
interface EmojiPickerProps {
  /** MUI 시절 Popper 의 anchorEl. 이제는 열림 여부 신호로만 쓰인다. */
  anchorEl: HTMLElement | null;
  onEmojiPick: (emoji: string) => void;
  onClose: () => void;
}

const EmojiPicker = (props: EmojiPickerProps) => {
  const { t } = useTranslation();
  const open = Boolean(props.anchorEl);
  const [search, setSearch] = useState("");
  const searchRef = useRef<HTMLInputElement>(null);
  const searchFields = splitNoEmpty(search.toLowerCase(), " ");

  const handleSearchClear = () => {
    setSearch("");
    searchRef.current?.focus();
  };

  return (
    <Dialog open={open} onOpenChange={(nextOpen) => !nextOpen && props.onClose()}>
      <DialogContent
        data-ntfy-close-on-back="true"
        aria-describedby={undefined}
        className="w-[min(380px,calc(100vw-32px))] gap-0 p-4 pb-2"
      >
        <DialogTitle className="sr-only">{t("emoji_picker_search_placeholder")}</DialogTitle>
        <div className="relative mr-8">
          <Input
            ref={searchRef}
            placeholder={t("emoji_picker_search_placeholder")}
            value={search}
            onChange={(ev) => setSearch(ev.target.value)}
            type="text"
            role="searchbox"
            aria-label={t("emoji_picker_search_placeholder")}
            className={search ? "pr-9" : undefined}
          />
          {search && (
            <Button
              type="button"
              variant="ghost"
              size="icon"
              onClick={handleSearchClear}
              aria-label={t("emoji_picker_search_clear")}
              className="text-muted-foreground absolute top-1/2 right-1 size-7 -translate-y-1/2"
            >
              <X />
            </Button>
          )}
        </div>
        <div className="mt-3 max-h-[300px] overflow-y-auto">
          <div className="flex flex-wrap">
            {Object.keys(emojisByCategory).map((category) => (
              <Category
                key={category}
                title={category}
                emojis={emojisByCategory[category]}
                search={searchFields}
                onPick={props.onEmojiPick}
              />
            ))}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

interface CategoryProps {
  title: string;
  emojis: SearchableEmoji[];
  search: string[];
  onPick: (emoji: string) => void;
}

const Category = (props: CategoryProps) => {
  const showTitle = props.search.length === 0;
  return (
    <>
      {showTitle && <p className="mb-2 w-full">{props.title}</p>}
      {props.emojis.map((emoji) => (
        <Emoji key={emoji.aliases[0]} emoji={emoji} search={props.search} onClick={() => props.onPick(emoji.aliases[0])} />
      ))}
    </>
  );
};

const emojiMatches = (emoji: SearchableEmoji, words: string[]) =>
  words.length === 0 || words.some((word) => emoji.searchBase.includes(word));

interface EmojiProps {
  emoji: SearchableEmoji;
  search: string[];
  onClick: () => void;
}

const Emoji = (props: EmojiProps) => {
  const { emoji } = props;
  const matches = emojiMatches(emoji, props.search);
  const title = `${emoji.description} (${emoji.aliases[0]})`;
  return (
    <div
      onClick={props.onClick}
      title={title}
      aria-label={title}
      className="my-2 mr-2 size-[30px] cursor-pointer text-[30px] leading-[30px] opacity-85 hover:opacity-100"
      style={{ display: matches ? "" : "none" }}
    >
      {emoji.emoji}
    </div>
  );
};

export default EmojiPicker;
